import clsx from 'clsx';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router';

import useBestDealsAisleQuery from 'api/queries/useBestDealsAisleQuery';
import { usePreviouslyPurchasedQuery } from 'api/queries/usePreviouslyPurchasedQuery';
import useRescuedAisleQuery from 'api/queries/useRescuedAisleQuery';
import { NEW_AISLES } from 'constants/shop';
import Tag from 'honeydew/Tag';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook';
import {
    buildDesktopAisleSectionList,
    getBestDealsAisleItemCount,
    getDesktopCuratedNavItems,
    getRescuedAisleItemCount,
} from 'utils/aislesUtils';
import { trackAisleNavigation } from 'utils/analyticsUtils';
import { getAislePath } from 'utils/pathUtils';
import twclsx from 'utils/twclsx';

const styles = {
    navList: 'mt-20 space-y-10',
    navLink: 'block p-10',
    navLinkNew: 'flex items-center gap-15',
    navLinkActive: 'bg-lime if:bg-strawberry',
    navDivider: 'my-20 -mx-20 h-[3px] border-none bg-lime if:bg-strawberry',
    navHeadline: 'text-body-lg font-grotesk-bold uppercase',
};

export function DesktopShopNavigation({ aisles, className, inStockAllItemsCount }) {
    const { plusMembershipBranding } = useAccountPlusMembershipHook();
    const { data: prevPurchasedData, isLoading: prevPurchasedIsLoading } = usePreviouslyPurchasedQuery();
    const { data: bestDealsAislePayload } = useBestDealsAisleQuery();
    const { data: rescuedAislePayload } = useRescuedAisleQuery();
    const bestDealsAisleInStockCount = getBestDealsAisleItemCount(bestDealsAislePayload);
    const rescuedAisleInStockCount = getRescuedAisleItemCount(rescuedAislePayload);

    const prevPurchasedItems = prevPurchasedData?.items?.[0] ?? [];

    const showBuyItAgainAisle = !prevPurchasedIsLoading && prevPurchasedItems.length > 0;
    const showShopWithPoints = !prevPurchasedIsLoading;

    const curatedNavItems = getDesktopCuratedNavItems({
        showBuyItAgain: showBuyItAgainAisle,
        showShopWithPoints,
        plusMembershipBranding,
    });

    const desktopAisleSectionItems = buildDesktopAisleSectionList(
        aisles,
        inStockAllItemsCount,
        bestDealsAisleInStockCount,
        rescuedAisleInStockCount
    );

    const curatedLinks = curatedNavItems.map((item) => (
        <li key={item.id}>
            <NavLink
                to={item.url}
                end={item.navLinkEnd ?? false}
                className={({ isActive }) =>
                    clsx(
                        item.navLinkEnd ? styles.navLink : [styles.navLink, styles.navLinkNew],
                        item.navLinkEnd ? { [styles.navLinkActive]: isActive } : { 'bg-brand-secondary': isActive }
                    )
                }
                onClick={() =>
                    trackAisleNavigation({
                        aisleName: item.analyticsAisleName ?? item.name,
                        level: 1,
                        mobile: false,
                        uiElement: 'desktop L1',
                    })
                }
            >
                {item.name}
            </NavLink>
        </li>
    ));

    const aisleItems = desktopAisleSectionItems.map((aisle) => (
        <li key={aisle.id}>
            <NavLink
                to={aisle.url || getAislePath(aisle.name)}
                className={({ isActive }) => clsx(styles.navLink, { [styles.navLinkActive]: isActive })}
                onClick={() => {
                    trackAisleNavigation({
                        aisleName: aisle.name,
                        level: 1,
                        mobile: false,
                        uiElement: 'desktop L1',
                    });
                }}
            >
                {aisle.name}{' '}
                <span
                    className={clsx('text-grey', {
                        'mr-10': NEW_AISLES.includes(aisle.name),
                    })}
                >
                    ({aisle.inStockCount})
                </span>
                {NEW_AISLES.includes(aisle.name) && (
                    <Tag bgColor="special" variant="dark">
                        NEW
                    </Tag>
                )}
            </NavLink>
        </li>
    ));

    return (
        <nav
            className={twclsx(
                'fixed top-80 bottom-0 left-0 w-shop-sidenav shrink-0 overflow-y-scroll bg-malt p-20 if:bg-oat',
                className
            )}
        >
            <h3 className={styles.navHeadline}>Curated</h3>
            <ul className={styles.navList}>{curatedLinks}</ul>
            <hr className={styles.navDivider} />
            <h3 className={styles.navHeadline}>Aisles</h3>
            <ul className={styles.navList}>{aisleItems}</ul>
        </nav>
    );
}

DesktopShopNavigation.propTypes = {
    aisles: PropTypes.arrayOf(PropTypes.shape({})),
};

DesktopShopNavigation.defaultProps = {
    aisles: [],
};
